import type { FlightPlayerScoresFragment } from '../../../__generated__/hooks'
import type { CurrentPlayerCtx } from '../../../PlayerShell'
import { blockSideTotals } from '../helpers'

const FORMAT_LABEL: Record<string, string> = {
  SCRAMBLE: 'Scramble',
  BESTBALL: 'Best ball',
  FOURSOME: 'Foursome',
  GOLF: 'Singel',
}

type Block = {
  ordinal: number
  fromHole: number
  toHole: number
  format: string
}

/**
 * Your side's round, etappe by etappe, and the figure the leaderboard ranks on.
 *
 * The block cards above are four columns of individual scores, and on a split
 * round no column is the side's result: best ball throws one of each pair's
 * two away, and the singles block keeps both. So the card could be read end to
 * end without ever meeting the number the round is decided by — it lived only
 * on the leaderboard, with nothing on the card to check it against.
 *
 * Net, not gross, as the figure on the right, because that is what the
 * leaderboard ranks; the gross beside it is what the columns above add up to,
 * so both roads in are visible.
 */
const SideSummary = ({
  blocks,
  flight,
  player,
}: {
  blocks: Block[]
  flight: FlightPlayerScoresFragment[]
  player: CurrentPlayerCtx
}) => {
  const sideId = player.team?.id
  if (!sideId) return null
  // Both axes: a one-ball block's entry IS your team, while in the other two
  // your side is a pair of player entries carrying the team.
  const yours = flight.filter((e) => e.id === sideId || e.team?.id === sideId)
  if (yours.length === 0) return null

  const rows = blocks.map((block) => ({
    block,
    totals: blockSideTotals(
      yours.filter((e) =>
        (e.scores ?? []).some(
          (s) =>
            s.hole.number >= block.fromHole && s.hole.number <= block.toHole
        )
      ),
      block
    ),
  }))
  const gross = rows.reduce((sum, r) => sum + r.totals.gross, 0)
  const net = rows.reduce((sum, r) => sum + r.totals.net, 0)
  const counted = rows.reduce((sum, r) => sum + r.totals.counted, 0)
  // What a finished card owes: the one-ball and best-ball blocks want one
  // score per hole, the singles block one per partner.
  const expected = blocks.reduce((sum, b) => {
    const holes = b.toHole - b.fromHole + 1
    return (
      sum +
      (b.format === 'GOLF'
        ? holes * (player.team?.players?.length ?? 2)
        : holes)
    )
  }, 0)

  return (
    <div
      className="mx-2 mt-4 rounded-lg shadow-sm overflow-hidden"
      style={{ background: 'var(--cream-card)' }}
    >
      <div className="px-3 py-2 bg-green-900 text-paper flex items-baseline justify-between">
        <span
          className="text-[10px] font-bold"
          style={{ letterSpacing: 'var(--ls-caps)' }}
        >
          DITT LAG
        </span>
        <span className="text-[11px] font-semibold truncate pl-2">
          {player.team?.name}
        </span>
      </div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-[9px] uppercase tracking-wider text-ink-500">
            <th scope="col" className="text-left px-3 pt-2">
              <span className="sr-only">Etappe</span>
            </th>
            <th className="text-right px-2 pt-2 font-semibold">Brutto</th>
            <th className="text-right px-3 pt-2 font-semibold">Netto</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ block, totals }) => (
            <tr key={block.ordinal} className="border-t border-ink-100">
              <th scope="row" className="text-left px-3 py-1.5 font-normal">
                <span className="text-xs font-semibold text-ink-900">
                  {FORMAT_LABEL[block.format] ?? block.format}
                </span>
                <span className="font-mono num text-[10px] text-ink-500 ml-1.5">
                  {block.fromHole}–{block.toHole}
                </span>
              </th>
              <td className="text-right px-2 py-1.5 font-mono num text-sm text-ink-700">
                {totals.gross || '—'}
              </td>
              <td className="text-right px-3 py-1.5 font-mono num text-sm font-semibold text-ink-900">
                {totals.net || '—'}
              </td>
            </tr>
          ))}
          <tr
            className="border-t border-ink-200"
            style={{ background: 'var(--green-50)' }}
          >
            <th
              scope="row"
              className="text-left px-3 py-2 font-display text-[11px] font-extrabold text-green-900"
              style={{
                letterSpacing: 'var(--ls-caps)',
                textTransform: 'uppercase',
              }}
            >
              Totalt
            </th>
            <td className="text-right px-2 py-2 font-mono num text-sm text-ink-700">
              {gross || '—'}
            </td>
            <td className="text-right px-3 py-2 font-mono num text-base font-bold text-green-900">
              {net || '—'}
            </td>
          </tr>
        </tbody>
      </table>
      {/*
        The count, because the total is not an 18-hole score and a bare number
        invites being read as one: the singles etappe counts both partners, so
        a finished card is 24 scores over 18 holes.
      */}
      <p className="px-3 pb-2 pt-1 text-[10px] text-ink-500 num">
        {counted}/{expected} tellende scorer · nettotallet er det leaderboardet
        rangerer på
      </p>
    </div>
  )
}

export default SideSummary
